/**
 * A scan's triggers for one symbol, painted on the price chart as yellow circles.
 *
 * <h2>Yellow, and a circle, on purpose</h2>
 *
 * The trade decoration already owns green and red for outcome and a triangle for direction. These
 * are not outcomes — nothing has happened yet — so reusing either channel would say something the
 * data does not support. A yellow circle is the chart's way of saying "pending": no direction in
 * the shape, no verdict in the colour.
 *
 * <p>The direction is still readable, because it decides which SIDE of the bar the circle sits on
 * and it is written in the tooltip. Anyone who needs it can get it; nobody is told a trade won.
 *
 * <h2>THE COLUMN A FUTURE SIGNAL LANDS ON</h2>
 *
 * A pending trigger is often dated past the newest bar — that is what makes it pending. The chart
 * does not render the raw series: it renders `extendBarData(barData, 250)`, which appends 250
 * WEEKDAY placeholder columns at the last close. So a valid spatial column usually does exist for a
 * future date, and the bug this replaced was building the overlay against the UNPADDED series: the
 * date failed the membership test, the circle was dropped, and the trigger a desk had opened the
 * chart to look at was the one thing not on it.
 *
 * <p>Two cases, and one refusal:
 *
 * <pre>
 *   exact match in the rendered dates   use it
 *   no match, but later columns exist   snap FORWARD to the first one at or after it
 *   past the last rendered column       DROPPED — the axis is padded to reach it instead
 * </pre>
 *
 * The middle case is not hypothetical: the padding skips Saturdays and Sundays, so a trigger dated
 * on a weekend has no column of its own even though it is well inside the padded range.
 *
 * <h2>BACKWARD ANCHORING IS GONE</h2>
 *
 * A signal past the last column used to be drawn on that last column. That put a gold circle on
 * TODAY'S candle for a trigger dated weeks ahead — a phantom, indistinguishable from a signal that
 * really fired today, and worse than showing nothing because it invents an event on a bar a reader
 * is actively studying.
 *
 * <p>The axis is padded to reach the furthest signal instead, so the mark plots in empty space on
 * its own chronological date. If padding still cannot reach it, the point is DROPPED and counted —
 * a missing mark is visible as a discrepancy; a phantom one is not.
 *
 * <p>An anchored point is marked `anchored: true` and its tooltip names its real date against the
 * column it sits on. That only ever happens FORWARD now, by at most a weekend.
 *
 * <h2>Pure</h2>
 *
 * Signals and the chart's bars in, an overlay out. No network, no ECharts, no React.
 */

/**
 * GOLD, and one colour whichever way the trigger points.
 *
 * <p>Gold rather than yellow, deliberately. Pure yellow collides with the warning states MUI uses
 * elsewhere in this UI and washes out against a pale chart background; an amber holds its edge on
 * both themes and reads as a distinct mark rather than as an alert.
 *
 * <p>It is also nothing like the trade decoration's green and red, which is the point: those mean
 * OUTCOME, and a pending trigger has none yet.
 *
 * <h2>Brightened from #d4a017, and this file is now the only place it lives</h2>
 *
 * The old gold sat close enough to the chart's own background tones that the mark a reader opens
 * the study FOR was the quietest thing on screen — it needed the dark rim beside it to be found at
 * all. `#ffab00` carries on its own.
 *
 * <p><b>It was defined TWICE</b>, here and in `gannConfluenceOverlay.js`, each with a comment
 * saying the two studies share one vocabulary for "this has not happened yet". They did, by
 * coincidence of both files holding the same literal. Changing one would have split the vocabulary
 * silently and left half the chart's pending marks in the old colour, which is the failure the
 * comments were written to prevent. That file imports this one now.
 *
 * <p>Still in the gold family rather than a true neon, and that is the constraint: green and red
 * are the trade decoration's outcome colours and purple belongs to the pivot overlays, so a pending
 * mark has to be prominent WITHOUT borrowing a hue that already means something.
 */
export const PENDING_COLOR = '#ffab00';

/**
 * How much smaller the golden circle is drawn than the marker it stands in for.
 *
 * <p>Twenty percent. The circle is a substitution for a directional triangle on a column the market
 * has not traded, and at the triangle's own size it dominated the bar it makes the WEAKEST claim
 * about. Smaller and brighter says "look here, nothing has happened yet" better than larger and
 * duller did.
 */
export const GOLDEN_CIRCLE_SCALE = 0.8;

/**
 * The golden circle's diameter, derived from the marker size it replaces.
 *
 * <p>Derived rather than written out, because the two draw sites start from different sizes — a CTS
 * mark is 18px because it can carry an index digit inside it, an Integral Delta pending mark is
 * 12px because it cannot. One literal would have to be wrong for one of them.
 *
 * <p><b>Floored at 8</b>, and the floor is the reason this is a function rather than a
 * multiplication at each site. A filled disc with a rim below about eight pixels reads as a smudge
 * — and at 18 * 0.8 = 14 a digit still clears the 11px that the CTS shape was widened past when the
 * digit was added, so the shrink cannot quietly reintroduce that overflow.
 */
export function goldenCircleSize(base) {
  const n = Number(base);
  if (!Number.isFinite(n) || n <= 0) return 8;
  return Math.max(8, Math.round(n * GOLDEN_CIRCLE_SCALE));
}

export const PENDING_SIGNALS_OVERLAY_ID = 'integral-delta-pending';

/** How each catalyst reads in a tooltip. Unknown keys fall through to the raw name. */
export const CATALYST_LABELS = {
  PTA_CROSS: 'PTA boundary cut',
  PTB_CROSS: 'PTB boundary cut',
  PTA_REVERSAL: 'PTA rejection',
  CTS_EXHAUSTION: 'CTS momentum',
};

export const catalystLabel = c => CATALYST_LABELS[c] || c || 'unknown';

const fmt = (v, dp = 4) => (Number.isFinite(v) ? v.toFixed(dp) : '—');

/**
 * What KIND of column a pending signal wants, named rather than inferred.
 *
 * <pre>
 *   HISTORICAL  the date is a real traded bar
 *   EXTENDED    the date is past the last traded bar, on one of the projection columns
 *   MISSING     the date is inside the charted span but has no column — a weekend or a holiday
 *   OFF_CHART   there is nowhere on this chart for it at all
 * </pre>
 *
 * <p>Carried on the point so the renderer never has to infer it. Inference is exactly how a
 * pending trigger on a projection column came to be styled like a historical trade: the marker
 * looked like any other, so it was treated like any other.
 */
export const PLACEMENT = {
  HISTORICAL: 'historical',
  EXTENDED: 'extended',
  MISSING: 'missing',
  OFF_CHART: 'offChart',
};

/**
 * Which rendered column a signal's date belongs in, and what kind of column that is.
 *
 * <p>Dates here are `yyyy-MM-dd`, which sorts lexicographically, so a string compare is a date
 * compare and no parsing is needed.
 *
 * @param date          the signal's own date
 * @param renderedDates the columns the chart actually draws, padding included
 * @param lastRealDate  the newest TRADED bar. Everything after it is projection. Optional: without
 *                      it nothing can be called EXTENDED, because there is no way to tell a
 *                      projection column from a traded one by its date alone.
 */
export function columnFor(date, renderedDates, lastRealDate) {
  if (!Array.isArray(renderedDates) || renderedDates.length === 0) return null;

  const kindOf = (column) => {
    if (lastRealDate && column > lastRealDate) return PLACEMENT.EXTENDED;
    return PLACEMENT.HISTORICAL;
  };

  if (renderedDates.includes(date)) {
    return { column: date, anchored: false, placement: kindOf(date) };
  }
  // Forward, not nearest: a trigger is actionable from its date onward, so the honest column is
  // the first session at or after it. Snapping backwards would place a future signal in the past.
  const next = renderedDates.find(d => d >= date);
  if (next !== undefined) {
    // The date is inside the charted span but has no column of its own — a weekend or a holiday.
    // It is MISSING whether or not the column it lands on is a projection one, because what makes
    // it special is that the signal's own date is absent from the axis.
    return { column: next, anchored: true, placement: PLACEMENT.MISSING };
  }
  // Beyond even the padding, and there is NO honest column for it. Refused rather than dragged
  // back onto the last bar, which would put a phantom mark on a candle a reader is studying.
  // padBarsNeededFor exists so this should not happen; when it does, it is counted, not drawn.
  return { column: null, anchored: false, placement: PLACEMENT.OFF_CHART };
}

/**
 * How many EXTRA weekday columns the axis needs to reach the furthest signal.
 *
 * <p>Weekdays, matching extendBarData's own padding rule, plus a small cushion so the furthest
 * mark is not jammed against the right edge. Returns 0 when nothing reaches past the axis.
 *
 * <p>This is the whole of "pad the axis to the signal rather than the signal to the axis". The
 * caller extends by the larger of its normal projection and this, so the ordinary chart is
 * unchanged and a distant trigger still gets a column of its own.
 */
export function padBarsNeededFor(signals, renderedDates, { cushion = 5 } = {}) {
  if (!Array.isArray(renderedDates) || renderedDates.length === 0) return 0;
  const last = renderedDates[renderedDates.length - 1];
  const furthest = (Array.isArray(signals) ? signals : [])
    .filter(s => s && s.date)
    .reduce((max, s) => (s.date > max ? s.date : max), last);
  if (furthest <= last) return 0;

  // Counted rather than divided by 7/5: a calendar estimate is off by a day or two per month, and
  // being short by one column is the difference between a mark on its own date and no mark at all.
  let weekdays = 0;
  const d = new Date(`${last}T12:00:00Z`);
  const end = new Date(`${furthest}T12:00:00Z`);
  while (d < end && weekdays < 5000) {
    d.setUTCDate(d.getUTCDate() + 1);
    const day = d.getUTCDay();
    if (day !== 0 && day !== 6) weekdays += 1;
  }
  return weekdays + cushion;
}

/**
 * Build the overlay for one symbol's pending triggers.
 *
 * @param {Array}  signals  ActionableSignal objects as the scan file stores them
 * @param {object} barData  the series the CHART renders, padding included — NOT the raw bars.
 *                          Passing the unpadded series silently drops every future trigger.
 *
 * <p>A trigger with no column of its own is anchored forward and flagged, rather than dropped.
 * That is a change from the trade decoration, which drops instead — and the asymmetry is the
 * point: a historical trade has a real bar or it is a mistake, whereas a pending trigger is
 * SUPPOSED to be dated past the data, so dropping it would hide the only thing worth seeing.
 */
export function buildPendingSignalsOverlay(signals, barData, { lastRealDate } = {}) {
  // The dates the CHART renders, which include extendBarData's weekday padding. Building against
  // the unpadded series is what dropped every future trigger.
  const dates = barData && Array.isArray(barData.dates) ? barData.dates : [];
  const list = (Array.isArray(signals) ? signals : [])
    .filter(s => s && s.date && Number.isFinite(s.triggerPrice));

  const points = [];
  list.forEach((s) => {
    const slot = columnFor(s.date, dates, lastRealDate);
    // No columns at all, or none this signal can honestly occupy. Counted by countUndrawn.
    if (!slot || !slot.column) return;
    const isLong = s.direction === 'LONG';
    points.push({
      // The COLUMN, which is the signal's own date unless it had none of its own.
      date: slot.column,
      // Y is strictly the trigger price. Never the placeholder bar's close — the padded columns
      // are flat at the last real close, so using them would draw every pending trigger on one
      // horizontal line regardless of where it actually fires.
      price: s.triggerPrice,
      // Below the bar for a long, above for a short — the same convention the trade arrows use, so
      // the two decorations can share a chart without the reader having to learn a second rule.
      position: isLong ? 'below' : 'above',
      // AN ARROW, NOT A CIRCLE, and this is a deliberate exception to the gold-circle rule.
      //
      // The circle exists to say "no direction is being claimed" for marks whose direction is not
      // known - a forward CTS confluence, say, whose shape a flat placeholder bar cannot tell us.
      // A SCAN signal is the opposite case: the whole artefact is a resolved LONG or SHORT, that
      // resolution is the product, and drawing it as a shape that announces "direction unknown"
      // throws away the one thing the row is for.
      //
      // The final gate (utils/forceFutureMarkerStyle) therefore skips these - see the exemption
      // there, which keys on this overlay's id rather than on the shape, so a future change of
      // shape cannot silently re-arm the override.
      symbol: 'triangle',
      // 'triangle' points UP already, so only a short is rotated.
      ...(isLong ? {} : { symbolRotate: 180 }),
      symbolSize: 12,
      color: PENDING_COLOR,
      anchored: slot.anchored,
      // Named on the point, so the renderer styles from a fact rather than from a guess.
      placement: slot.placement,
      tooltipLines: [
        `<b>PENDING ${s.direction} — ${s.symbol}</b>`,
        `  ${catalystLabel(s.catalyst)} on ${s.date}`,
        `  trigger ${fmt(s.triggerPrice)}`,
        ...(slot.anchored
          ? [`  (no bar for ${s.date}; drawn on ${slot.column})`]
          : []),
        ...(s.detail ? [`  ${s.detail}`] : []),
      ],
    });
  });

  return {
    id: PENDING_SIGNALS_OVERLAY_ID,
    type: 'pendingsignals',
    label: 'Pending triggers',
    points,
    drawnCount: points.length,
    /** How many are sitting in a column that is not their own date. */
    anchoredCount: points.filter(p => p.anchored).length,
    /** Whether the chart had any columns to place onto — distinguishes the two empty outcomes. */
    hasColumns: dates.length > 0,
    /** How many sit on a projection column rather than a traded bar. */
    extendedCount: points.filter(p => p.placement === PLACEMENT.EXTENDED).length,
    /** How many wanted a date the axis does not have at all — a weekend or a holiday. */
    missingCount: points.filter(p => p.placement === PLACEMENT.MISSING).length,
    /**
     * Everything handed in that did not become a circle — malformed rows, and anything at all
     * when the chart has no columns yet. Stated rather than dropped, so a scan the chart cannot
     * place is never silent.
     */
    countUndrawn: (Array.isArray(signals) ? signals.length : 0) - points.length,
  };
}

/** The status line the chart shows when a scan's signals are painted. */
export function pendingOverlayStatus(symbol, overlay) {
  if (!overlay || overlay.drawnCount === 0) {
    // Now a DESIGNED outcome rather than an edge case: a trigger past the padded axis is refused
    // instead of anchored backwards, so this line is the only trace of it. It names the count for
    // that reason — "none" alone does not tell a reader whether there was one or forty.
    //
    // "The chart has no columns" and "the triggers are dated past the columns it has" are
    // different problems with different fixes — load a symbol, versus widen the projection — so
    // they get different sentences rather than one that covers both and diagnoses neither.
    const n = overlay?.countUndrawn || 0;
    if (overlay && overlay.hasColumns === false) {
      return `${symbol}: the chart has no columns yet, so none of the scan's triggers can be drawn.`;
    }
    return n > 0
      ? `${symbol}: ${n} pending trigger${n === 1 ? '' : 's'} could not be placed on this chart `
        + '— dated beyond the projected columns.'
      : `${symbol}: none of the scan's triggers could be placed on this chart.`;
  }
  const missing = overlay.countUndrawn > 0
    ? ` (${overlay.countUndrawn} could not be placed)` : '';
  // Anchored points are called out, not buried: the circle is in a column that is not its date,
  // and a reader comparing it against the bars deserves to know which ones those are.
  const anchored = overlay.anchoredCount > 0
    ? ` ${overlay.anchoredCount} sit on the nearest session rather than their own date.` : '';
  return `${symbol}: ${overlay.drawnCount} pending trigger`
    + `${overlay.drawnCount === 1 ? '' : 's'} painted in yellow${missing}.${anchored}`;
}
